import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AutosaveFormDetailsParams, AutoSaveOwnersDetailsStatus, AutosaveResponseData, BusinessInformationFormData, OwnersDiffData, SubmissionFormDiffData } from '../../form-details.model';
import { AutosaveBusinessInfoStepService } from './autosave-business-info-step.service';
import { AutosaveOwnerInfoStepService } from './autosave-owner-info-step.service';

@Injectable({
  providedIn: 'root'
})
export class AutosaveFormStepsService {
  constructor(
    private readonly businessInfoStepService: AutosaveBusinessInfoStepService,
    private readonly ownersInfoStepService: AutosaveOwnerInfoStepService
  ) { }

  saveAllSteps(diffData: SubmissionFormDiffData, params: AutosaveFormDetailsParams): Observable<AutosaveResponseData> {
    const businessDiffData: BusinessInformationFormData = diffData?.businessDiffData;
    const ownersDiffData: OwnersDiffData = diffData?.ownersDiffData;

    if (!params.accountId) {
      return this.createAll(params, businessDiffData, ownersDiffData);
    } else if (params.accountId && !params.applicationId) {
      return this.updateAccountAndCreateOthers(params, businessDiffData, ownersDiffData);
    } else {
      return this.updateAll(params, businessDiffData, ownersDiffData);
    }
  }

  private createAll(
    params: AutosaveFormDetailsParams,
    businessDiffData: BusinessInformationFormData,
    ownersDiffData: OwnersDiffData
  ): Observable<AutosaveResponseData> {
    return this.businessInfoStepService.createNewAccountAndApplication(params, businessDiffData).pipe(
      switchMap(updatedParams =>
        this.ownersInfoStepService.saveOwnersInformationStep(ownersDiffData, updatedParams, true).pipe(
          map((ownersStatus: AutoSaveOwnersDetailsStatus) => ({ params: updatedParams, ownersStatus }))
        )
      ))
  }

  private updateAccountAndCreateOthers(
    params: AutosaveFormDetailsParams,
    businessDiffData: BusinessInformationFormData,
    ownersDiffData: OwnersDiffData
  ): Observable<AutosaveResponseData> {
    return this.businessInfoStepService.updateAccountAndCreateNewApplication(params, businessDiffData).pipe(
      switchMap(updatedParams =>
        this.ownersInfoStepService.saveOwnersInformationStep(ownersDiffData, updatedParams, true).pipe(
          map((ownersStatus: AutoSaveOwnersDetailsStatus) => ({ params: updatedParams, ownersStatus }))
        )
      ))
  }

  private updateAll(
    params: AutosaveFormDetailsParams,
    businessDiffData: BusinessInformationFormData,
    ownersDiffData: OwnersDiffData
  ): Observable<AutosaveResponseData> {
    const businessObs = this.businessInfoStepService.updateExistingAccountAndApplication(params, businessDiffData);
    const ownersObs = this.ownersInfoStepService.saveOwnersInformationStep(ownersDiffData, params);
    return forkJoin({ updatedParams: businessObs, ownersStatus: ownersObs }).pipe(
      map(({ updatedParams, ownersStatus }) => ({ params: updatedParams, ownersStatus }))
    );
  }
}
